import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// REACT_APP_BACKEND_URL is a Create React App build-time variable — it's
// inlined into the static JS bundle when `npm run build` runs and can never
// change afterward without a rebuild. If it's missing at build time,
// `${BACKEND_URL}/api` silently becomes the literal string "undefined/api",
// producing URLs like https://moneymeetpay.com/undefined/api/... — every
// request 404s, but only once deployed, since dev builds normally always
// have it set locally. Fail loudly instead so a misconfigured production
// build is obvious immediately in the console rather than a silent,
// confusing 404 on every API call.
if (!BACKEND_URL) {
  // eslint-disable-next-line no-console
  console.error(
    "REACT_APP_BACKEND_URL is not set — this build was produced without it. " +
    "All API requests will fail. Rebuild with REACT_APP_BACKEND_URL=https://your-api-host " +
    "npm run build (see docs/handbook/03_Frontend.md)."
  );
}

export const API_BASE = `${BACKEND_URL || ""}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,   // send mm_session httpOnly cookie automatically
  timeout: 30000,          // 30 s — prevents hung requests leaving UI in permanent loading state
});

// Request interceptor: no manual token injection needed — the httpOnly
// mm_session cookie is sent automatically by the browser with withCredentials.
// Mobile clients (Flutter) supply their own Authorization header separately.
api.interceptors.request.use((config) => config);

let _isRefreshing = false;
let _refreshQueue = [];

function _drainRefreshQueue(err) {
  _refreshQueue.forEach((cb) => cb(err));
  _refreshQueue = [];
}

// Response interceptor: on 401, attempt a silent token refresh once.
// If the refresh succeeds, retry the original request.
// If the refresh fails (expired refresh token), redirect to login.
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config;

    if (err?.response?.status !== 401) return Promise.reject(err);

    // Avoid refresh loops on the auth endpoints themselves.
    const path = original?.url || "";
    if (
      path.includes("/auth/login") ||
      path.includes("/auth/register") ||
      path.includes("/auth/refresh")
    ) {
      return Promise.reject(err);
    }

    if (original._retried) {
      // Already tried refresh — redirect to login.
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith("/login") && !currentPath.startsWith("/register") && currentPath !== "/") {
        window.location.assign("/login");
      }
      return Promise.reject(err);
    }

    if (_isRefreshing) {
      // Queue concurrent requests while refresh is in flight.
      return new Promise((resolve, reject) => {
        _refreshQueue.push((refreshErr) => {
          if (refreshErr) return reject(refreshErr);
          resolve(api(original));
        });
      });
    }

    original._retried = true;
    _isRefreshing = true;

    try {
      // POST /auth/refresh with no body — the mm_refresh_session cookie is
      // sent automatically by the browser. The server rotates both cookies.
      await api.post("/auth/refresh");
      _drainRefreshQueue(null);
      return api(original);
    } catch (refreshErr) {
      _drainRefreshQueue(refreshErr);
      const currentPath = window.location.pathname;
      if (!currentPath.startsWith("/login") && !currentPath.startsWith("/register") && currentPath !== "/") {
        window.location.assign("/login");
      }
      return Promise.reject(refreshErr);
    } finally {
      _isRefreshing = false;
    }
  }
);
